import { useCallback, useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import {
  setUserId,
  trackChallengeEvent,
  trackChallengeParticipant,
  trackChallengePerformance,
  trackCoinTransaction,
  trackError,
  trackEvent,
  trackLeaderboardInteraction,
  trackNavigation,
  trackPageView,
  trackPortfolioChange,
  trackTaskEvent,
  trackTradingEvent,
  trackUserEngagement,
  trackUserSession,
} from '@/lib/analytics';

export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (!import.meta.env.DEV) {
      trackPageView(location.pathname + location.search, document.title);
    }
  }, [location]);
};

export const useUserEngagement = () => {
  const trackEngagement = useCallback((engagementTimeMs: number) => {
    if (!import.meta.env.DEV) {
      trackUserEngagement(engagementTimeMs);
    }
  }, []);

  return { trackEngagement };
};

export const useUserTracking = () => {
  const setUser = useCallback((userId: string) => {
    if (!import.meta.env.DEV) {
      setUserId(userId);
    }
  }, []);

  return { setUser };
};

export const useEventTracking = () => {
  const trackCustomEvent = useCallback(
    (action: string, category: string, label?: string, value?: number) => {
      if (!import.meta.env.DEV) {
        trackEvent(action, category, label, value);
      }
    },
    []
  );

  const trackChallenge = useCallback(
    (
      action: 'view' | 'join' | 'trade' | 'complete',
      challengeId: string,
      challengeName?: string,
      value?: number
    ) => {
      if (!import.meta.env.DEV) {
        trackChallengeEvent(action, challengeId, challengeName, value);
      }
    },
    []
  );

  const trackTrading = useCallback(
    (
      action: 'buy' | 'sell' | 'hold',
      coinId: string,
      coinName?: string,
      amount?: number
    ) => {
      if (!import.meta.env.DEV) {
        trackTradingEvent(action, coinId, coinName, amount);
      }
    },
    []
  );

  const trackNav = useCallback((destination: string) => {
    if (!import.meta.env.DEV) {
      trackNavigation(destination);
    }
  }, []);

  const trackErrorEvent = useCallback((error: string, errorCode?: string) => {
    if (!import.meta.env.DEV) {
      trackError(error, errorCode);
    }
  }, []);

  return {
    trackCustomEvent,
    trackChallenge,
    trackTrading,
    trackNav,
    trackErrorEvent,
  };
};

export const useChallengeParticipantTracking = () => {
  const trackParticipant = useCallback(
    (
      action: 'join' | 'leave' | 'active' | 'inactive',
      challengeId: string,
      challengeName: string,
      participantId: string,
      participantType: 'new' | 'returning' | 'veteran',
      challengeDuration?: number
    ) => {
      if (!import.meta.env.DEV) {
        trackChallengeParticipant(
          action,
          challengeId,
          challengeName,
          participantId,
          participantType,
          challengeDuration
        );
      }
    },
    []
  );

  const trackPerformance = useCallback(
    (
      challengeId: string,
      challengeName: string,
      totalParticipants: number,
      activeParticipants: number,
      totalVolume: number,
      averageProfit: number,
      completionRate: number,
      challengeStatus: 'active' | 'completed' | 'cancelled'
    ) => {
      if (!import.meta.env.DEV) {
        trackChallengePerformance(
          challengeId,
          challengeName,
          totalParticipants,
          activeParticipants,
          totalVolume,
          averageProfit,
          completionRate,
          challengeStatus
        );
      }
    },
    []
  );

  return {
    trackParticipant,
    trackPerformance,
  };
};

export const useCoinTracking = () => {
  const trackTransaction = useCallback(
    (
      action: 'buy' | 'sell',
      coinId: string,
      coinName: string,
      coinSymbol: string,
      amount: number,
      price: number,
      totalValue: number,
      currency?: string,
      challengeId?: string,
      challengeName?: string
    ) => {
      if (!import.meta.env.DEV) {
        trackCoinTransaction(
          action,
          coinId,
          coinName,
          coinSymbol,
          amount,
          price,
          totalValue,
          currency,
          challengeId,
          challengeName
        );
      }
    },
    []
  );

  const trackPopularCoin = useCallback(
    (
      coinId: string,
      coinName: string,
      coinSymbol: string,
      totalTransactions: number,
      totalVolume: number,
      averagePrice: number,
      priceChange24h: number,
      marketCap?: number,
      rank?: number
    ) => {
      if (!import.meta.env.DEV) {
        trackPopularCoin(
          coinId,
          coinName,
          coinSymbol,
          totalTransactions,
          totalVolume,
          averagePrice,
          priceChange24h,
          marketCap,
          rank
        );
      }
    },
    []
  );

  return {
    trackTransaction,
    trackPopularCoin,
  };
};

export const useUserActivityTracking = () => {
  const trackSession = useCallback(
    (
      userId: string,
      sessionDuration: number,
      pagesVisited: number,
      challengesViewed: number,
      tradesMade: number,
      sessionType: 'active' | 'passive' | 'trading_focused'
    ) => {
      if (!import.meta.env.DEV) {
        trackUserSession(
          userId,
          sessionDuration,
          pagesVisited,
          challengesViewed,
          tradesMade,
          sessionType
        );
      }
    },
    []
  );

  const trackPortfolio = useCallback(
    (
      userId: string,
      totalValue: number,
      previousValue: number,
      changePercentage: number,
      topHoldings: Array<{
        coinId: string;
        coinName: string;
        value: number;
        percentage: number;
      }>,
      currency?: string
    ) => {
      if (!import.meta.env.DEV) {
        trackPortfolioChange(
          userId,
          totalValue,
          previousValue,
          changePercentage,
          topHoldings,
          currency
        );
      }
    },
    []
  );

  return {
    trackSession,
    trackPortfolio,
  };
};

export const useLeaderboardTracking = () => {
  const trackLeaderboard = useCallback(
    (
      action: 'view' | 'filter' | 'sort',
      leaderboardType: 'global' | 'challenge' | 'friends',
      filterCriteria?: string,
      sortBy?: string,
      challengeId?: string
    ) => {
      if (!import.meta.env.DEV) {
        trackLeaderboardInteraction(
          action,
          leaderboardType,
          filterCriteria,
          sortBy,
          challengeId
        );
      }
    },
    []
  );

  return {
    trackLeaderboard,
  };
};

export const useTaskTracking = () => {
  const trackTask = useCallback(
    (
      action: 'view' | 'start' | 'complete' | 'claim',
      taskId: string,
      taskTitle: string,
      taskCategory: 'social' | 'daily' | 'partners' | 'tasks' | 'achievements',
      reward?: number,
      userId?: string
    ) => {
      if (!import.meta.env.DEV) {
        trackTaskEvent(action, taskId, taskTitle, taskCategory, reward, userId);
      }
    },
    []
  );

  return {
    trackTask,
  };
};
